import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { RefreshCw } from "lucide-react";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Sparkles, Lock, Plus, Filter, Bot, BotOff } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import PhotoPicker from "@/components/PhotoPicker";
import FanProfile from "@/components/FanProfile";

const PANE_CLASS = {
  fanvue: "pane-fanvue",
  telegram: "pane-telegram",
  instagram: "pane-instagram",
  x: "pane-x",
};

const PLATFORM_LABEL = {
  fanvue: "Fanvue",
  telegram: "Telegram",
  instagram: "Instagram",
  x: "X",
};

const PLATFORM_DOT = {
  fanvue: "bg-emerald-400",
  telegram: "bg-sky-400",
  instagram: "bg-rose-400",
  x: "bg-white",
};

export default function Inbox() {
  const { t } = useT();
  const [params] = useSearchParams();
  const initialFan = params.get("fan");
  const [fans, setFans] = useState([]);
  const [activeFanvueFan, setActiveFanvueFan] = useState(null);
  const [activeOtherFan, setActiveOtherFan] = useState(null);
  const [otherPlatform, setOtherPlatform] = useState("telegram");
  const [filter, setFilter] = useState("");

  const loadFans = async () => {
    const r = await api.get("/fans");
    setFans(r.data);
  };
  // Okamžitá lokálna aktualizácia fanúšika v zozname (bez refetchu)
  const patchFanLocal = (u) =>
    setFans((prev) => prev.map((f) => (f.id === u.id ? { ...f, ...u } : f)));

  useEffect(() => {
    loadFans();
  }, []);

  useEffect(() => {
    if (!fans.length) return;
    if (initialFan) {
      const fan = fans.find((f) => f.id === initialFan);
      if (fan) {
        if (fan.platform === "fanvue") setActiveFanvueFan(fan);
        else {
          setOtherPlatform(fan.platform);
          setActiveOtherFan(fan);
        }
      }
    }
    if (!activeFanvueFan) {
      const f = fans.find((x) => x.platform === "fanvue");
      if (f) setActiveFanvueFan(f);
    }
    if (!activeOtherFan) {
      const f = fans.find((x) => x.platform === otherPlatform);
      if (f) setActiveOtherFan(f);
    }
    // eslint-disable-next-line
  }, [fans]);

  const fanvueFans = useMemo(
    () => fans.filter((f) => f.platform === "fanvue" && f.name.toLowerCase().includes(filter.toLowerCase())),
    [fans, filter]
  );

  const otherFans = useMemo(
    () => fans.filter((f) => f.platform === otherPlatform && f.name.toLowerCase().includes(filter.toLowerCase())),
    [fans, otherPlatform, filter]
  );

  return (
    <div className="h-screen flex flex-col">
      {/* Top bar */}
      <div className="px-6 py-3 border-b border-white/5 flex items-center justify-between gap-3 bg-[rgb(12,13,18)]">
        <div className="flex items-center gap-3">
          <div className="font-serif text-2xl leading-none">{t("inbox")}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">/ dual pane</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="filter fans"
              data-testid="inbox-filter"
              className="h-9 w-44 bg-white/[0.03] border-white/10 rounded-none pl-8 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Dual pane */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
        {/* LEFT: Fanvue */}
        <Pane
          paneClass={PANE_CLASS.fanvue}
          platform="fanvue"
          label="Fanvue"
          color="emerald"
          fans={fanvueFans}
          activeFan={activeFanvueFan}
          setActiveFan={setActiveFanvueFan}
          onFansChange={loadFans} onFanPatched={patchFanLocal}
        />
        {/* RIGHT: Other (switchable) */}
        <Pane
          paneClass={PANE_CLASS[otherPlatform]}
          platform={otherPlatform}
          label={PLATFORM_LABEL[otherPlatform]}
          color={otherPlatform === "telegram" ? "sky" : otherPlatform === "instagram" ? "rose" : "white"}
          fans={otherFans}
          activeFan={activeOtherFan}
          setActiveFan={setActiveOtherFan}
          onFansChange={loadFans} onFanPatched={patchFanLocal}
          platformSwitcher={
            <select
              value={otherPlatform}
              onChange={(e) => {
                setOtherPlatform(e.target.value);
                setActiveOtherFan(null);
              }}
              data-testid="other-platform-switch"
              className="bg-transparent border border-white/10 rounded-none text-xs uppercase tracking-widest px-2 py-1 text-white"
            >
              <option value="telegram">Telegram</option>
            </select>
          }
        />
      </div>
    </div>
  );
}

function Pane({ paneClass, platform, label, color, fans, activeFan, setActiveFan, onFansChange, onFanPatched, platformSwitcher }) {
  const { t } = useT();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadMessages = async (fanId) => {
    const r = await api.get("/messages", { params: { fan_id: fanId } });
    setMessages(r.data);
  };

  useEffect(() => {
    if (activeFan) {
      loadMessages(activeFan.id);
      setDraft(null);
    } else {
      setMessages([]);
    }
  }, [activeFan]);

  // Live režim: otvorený chat sa obnovuje každých 10 s, zoznam fanúšikov každých 30 s
  useEffect(() => {
    if (!activeFan?.id) return;
    const t = setInterval(() => loadMessages(activeFan.id), 10000);
    return () => clearInterval(t);
  }, [activeFan?.id]);
  useEffect(() => {
    const t = setInterval(() => onFansChange?.(), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const accentText = {
    emerald: "text-emerald-400",
    sky: "text-sky-400",
    rose: "text-rose-400",
    white: "text-white",
  }[color];

  const sendNow = async (msgText) => {
    if (!msgText.trim() || !activeFan) return;
    setSending(true);
    try {
      await api.post("/messages/send", { fan_id: activeFan.id, text: msgText });
      setText("");
      setDraft(null);
      await loadMessages(activeFan.id);
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const simulateIncoming = async () => {
    if (!activeFan) return;
    const sampleMsg = prompt(
      `Simulovaná správa od ${activeFan.name} (len sa zapíše do chatu — AI na ňu sama neodpovie):`
    );
    if (!sampleMsg) return;
    await api.post("/messages/incoming", {
      fan_external_id: activeFan.external_id,
      fan_name: activeFan.name,
      text: sampleMsg,
      from_creator: false,
      platform: activeFan.platform,
      no_ai: true, // nikdy neodosielať — od toho je tlačidlo „AI návrh"
    });
    toast.success("Správa pridaná — klikni „AI návrh“ pre odpoveď");
    await loadMessages(activeFan.id);
    onFansChange?.();
  };

  const generateDraft = async () => {
    if (!activeFan) return;
    setGenerating(true);
    try {
      const lastIncoming = [...messages].reverse().find((m) => !m.from_creator);
      const r = await api.post("/ai/draft", {
        fan_id: activeFan.id,
        text: lastIncoming?.text || "hey baby 💜",
        auto_send: false,
      });
      setDraft(r.data);
    } catch (e) {
      toast.error("AI draft failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className={`${paneClass} flex flex-col h-[calc(100vh-49px)] border-r border-white/5 last:border-r-0`}>
      {/* Pane header */}
      <header className="px-5 py-3 border-b border-white/5 flex items-center justify-between bg-black/30 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <div className={`w-1.5 h-1.5 rounded-full ${PLATFORM_DOT[platform]} animate-pulse`} />
          <div className={`text-[10px] uppercase tracking-[0.3em] ${accentText} font-medium`}>{label}</div>
        </div>
        {platformSwitcher}
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Fan list */}
        <aside className="w-56 shrink-0 border-r border-white/5 overflow-y-auto bg-black/20">
          <button
            onClick={async () => {
              try {
                const r = await api.post("/connections/fanvue/sync");
                (r.data.note
        ? toast.warning(`Sync: ${r.data.fans ?? 0} fans, ${r.data.messages ?? 0} správ · problém: ${r.data.note}`, { duration: 10000 })
        : toast.success(`Sync: ${r.data.fans ?? 0} fanúšikov, ${r.data.messages ?? 0} správ`));
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Sync zlyhal");
              }
              await loadFans();
            }}
            data-testid="inbox-refresh"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-fuchsia-300 border-b border-white/5 hover:bg-fuchsia-500/10 transition-colors"
          >
            <RefreshCw size={11} /> refresh
          </button>
          {fans.length === 0 && (
            <div className="p-4 text-xs text-white/30 text-center">{t("no_fans_yet")}</div>
          )}
          {fans.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFan(f)}
              data-testid={`fan-item-${f.id}`}
              className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${
                activeFan?.id === f.id ? "bg-white/[0.06]" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-serif text-base text-white truncate flex items-center gap-1.5">
                  {f.name}
                  {f.nsfw_unlocked && <Lock size={9} className="text-rose-400 -rotate-12" />}
                  {f.ai_enabled === false && <BotOff size={10} className="text-white/30" title="AI off" />}
                </div>
                <div className={`text-xs tabular-nums font-medium ${accentText}`}>
                  ${(f.spend_total || 0).toFixed(0)}
                </div>
              </div>
              {f.last_message_at && (
                <div className="text-[10px] text-white/30 mt-0.5">
                  {new Date(f.last_message_at).toLocaleString()}
                </div>
              )}
            </button>
          ))}
        </aside>

        {/* Chat */}
        <div className="flex-1 flex flex-col min-w-0">
          {!activeFan ? (
            <div className="flex-1 flex items-center justify-center text-white/30 font-serif italic text-xl">
              select a fan
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif text-xl truncate">{activeFan.name}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-widest flex items-center gap-2 flex-wrap">
                    <span>{activeFan.nsfw_unlocked ? "NSFW" : "SFW"}</span>
                    <span>· ${(activeFan.spend_total || 0).toFixed(2)}</span>
                    {activeFan.fan_status && <span className="text-emerald-300">· {activeFan.fan_status}</span>}
                    {activeFan.is_top_spender && <span className="text-amber-300">· TOP</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    {(activeFan.tags || []).map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-200 flex items-center gap-1">
                        {t}
                        <button
                          onClick={async () => {
                            const newTags = (activeFan.tags || []).filter((x) => x !== t);
                            const r = await api.patch(`/fans/${activeFan.id}`, { tags: newTags });
                            setActiveFan(r.data);
                            onFanPatched?.(r.data);
                          }}
                          className="text-fuchsia-300/60 hover:text-rose-300 ml-1"
                          data-testid={`tag-remove-${t}`}
                        >×</button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder="+ tag"
                      data-testid="input-add-tag"
                      onKeyDown={async (e) => {
                        if (e.key === "Enter" && e.currentTarget.value.trim()) {
                          const newTag = e.currentTarget.value.trim().toLowerCase();
                          const tags = Array.from(new Set([...(activeFan.tags || []), newTag]));
                          const r = await api.patch(`/fans/${activeFan.id}`, { tags });
                          setActiveFan(r.data);
                          onFanPatched?.(r.data);
                          e.currentTarget.value = "";
                        }
                      }}
                      className="bg-transparent border border-white/10 text-[10px] uppercase tracking-widest px-2 py-0.5 w-20 text-white placeholder:text-white/30 focus:outline-none focus:border-fuchsia-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FanProfile fan={activeFan} onUpdate={(updated) => { setActiveFan(updated); onFanPatched?.(updated); }} />
                  <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60 cursor-pointer" title="Toggle AI auto-reply for this fan only">
                    {activeFan.ai_enabled !== false ? (
                      <Bot size={12} className="text-fuchsia-400" />
                    ) : (
                      <BotOff size={12} className="text-white/30" />
                    )}
                    <span>AI</span>
                    <Switch
                      checked={activeFan.ai_enabled !== false}
                      onCheckedChange={async (v) => {
                        try {
                          const r = await api.patch(`/fans/${activeFan.id}`, { ai_enabled: v });
                          setActiveFan(r.data);
                          onFanPatched?.(r.data);
                          toast.success(v ? "AI zapnuté pre " + activeFan.name : "AI vypnuté pre " + activeFan.name);
                        } catch (e) {
                          toast.error("Toggle failed");
                        }
                      }}
                      data-testid={`switch-fan-ai-${activeFan.id}`}
                    />
                  </label>
                  <button
                    onClick={simulateIncoming}
                    className="text-[10px] uppercase tracking-widest text-white/50 hover:text-white border border-white/10 px-3 py-1.5"
                    data-testid="btn-simulate-incoming"
                  >
                    <Plus size={11} className="inline mr-1" />
                    incoming
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center text-white/30 text-sm font-serif italic mt-12">{t("no_messages")}</div>
                )}
                {messages.map((m) => (
                  <Bubble key={m.id} msg={m} accentText={accentText} />
                ))}
                {draft && <DraftBubble draft={draft} onUse={() => sendNow(draft.text)} onDismiss={() => setDraft(null)} />}
                {generating && (
                  <div className="ai-shimmer border-l-2 border-fuchsia-500 bg-fuchsia-500/5 p-3 rounded-r-sm text-sm text-white/60 font-serif italic">
                    she's thinking...
                  </div>
                )}
              </div>

              <div className="border-t border-white/5 p-3 bg-black/40 backdrop-blur">
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={generateDraft}
                    disabled={generating}
                    className="bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-300 border border-fuchsia-500/30 rounded-none px-3 h-10"
                    data-testid="btn-ai-draft"
                  >
                    <Sparkles size={14} className="mr-1.5" />
                    {t("ai_draft")}
                  </Button>
                  <PhotoPicker fan={activeFan} onSent={() => loadMessages(activeFan.id)} />
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendNow(text)}
                    placeholder={t("write_message")}
                    data-testid="input-message"
                    className="bg-white/[0.04] border-white/10 rounded-none h-10"
                  />
                  <Button
                    onClick={() => sendNow(text)}
                    disabled={sending || !text.trim()}
                    className="bg-white text-black hover:bg-white/90 rounded-none h-10 px-4"
                    data-testid="btn-send"
                  >
                    <Send size={14} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Bubble({ msg, accentText }) {
  const fromCreator = msg.from_creator;
  return (
    <div className={`flex ${fromCreator ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${
          fromCreator
            ? "bg-white/10 text-white rounded-l-lg rounded-br-lg"
            : "bg-white/[0.04] border border-white/10 text-white/90 rounded-r-lg rounded-bl-lg"
        }`}
      >
        <div className="whitespace-pre-wrap">{msg.text}</div>
        {msg.amount > 0 && (
          <div className={`text-[10px] mt-1 font-medium tabular-nums ${accentText}`}>
            +${msg.amount.toFixed(2)} tip
          </div>
        )}
        {msg.ppv_price && (
          <div className="text-[10px] mt-1 text-fuchsia-300 flex items-center gap-1">
            <Lock size={9} /> PPV unlock · ${msg.ppv_price.toFixed(2)}
            {msg.ppv_count > 1 && <span className="text-white/40">· {msg.ppv_count} fotiek</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function DraftBubble({ draft, onUse, onDismiss }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[78%] border-l-2 border-fuchsia-500 bg-fuchsia-500/10 p-3 rounded-r-sm text-sm text-white/90">
        <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 mb-1">AI DRAFT</div>
        <div className="whitespace-pre-wrap font-serif italic">{draft.text}</div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={onUse}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs uppercase tracking-widest px-3 py-1.5"
            data-testid="btn-use-draft"
          >
            send
          </button>
          <button
            onClick={onDismiss}
            className="text-white/40 hover:text-white text-xs uppercase tracking-widest px-3 py-1.5"
            data-testid="btn-dismiss-draft"
          >
            dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
